import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { validateContrast } from "@guardian/shared-utils";
import * as Sentry from "@sentry/react";
import { apiClient } from "@/services/api/client";
import { extractBrandSlug, hexToHslComponents } from "@/lib/branding";
import { BrandHydrationFallback } from "./BrandHydrationFallback";

export interface BrandBlock {
  primaryColor: string;
  secondaryColor: string;
  textOnPrimaryColor: string;
  displayName: string;
  logoUrl: string;
  slug: string;
}

interface BrandingContextValue {
  branding: BrandBlock | null;
  isLoading: boolean;
  isBranded: boolean;
  isRedirecting: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  isLoading: true,
  isBranded: false,
  isRedirecting: false,
});

export const useBranding = () => useContext(BrandingContext);

function hasActiveBrandFields(payload: unknown): payload is BrandBlock {
  if (typeof payload !== "object" || payload === null) return false;
  const p = payload as Record<string, unknown>;
  return (
    typeof p.primaryColor === "string" &&
    typeof p.secondaryColor === "string" &&
    typeof p.textOnPrimaryColor === "string" &&
    typeof p.displayName === "string" &&
    typeof p.logoUrl === "string" &&
    typeof p.slug === "string"
  );
}

const REDIRECT_KINDS = new Set(["SLUG_CHANGED", "TIER_DOWNGRADED", "ADMIN_REVOKED"]);
const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;

function hasRedirectFields(
  payload: unknown,
): payload is { redirectTo: string | null; redirectKind: string } {
  if (typeof payload !== "object" || payload === null) return false;
  const p = payload as Record<string, unknown>;
  if (typeof p.redirectKind !== "string") return false;
  if (!REDIRECT_KINDS.has(p.redirectKind as string)) return false;
  // # WHY validate slug shape at the SPA: window.location.replace target is interpolated
  // from redirectTo. The DB CHECK constraint prevents writing arbitrary values today, but
  // the SPA must not trust the wire — a future DB-bypass path that wrote
  // redirectTo="evil.com?x=" would interpolate to https://evil.com?x=.gcheckin.com/... (open redirect).
  if (p.redirectTo === null) return true;
  return typeof p.redirectTo === "string" && SLUG_PATTERN.test(p.redirectTo);
}

type ApplyOutcome = "applied" | "rejected_contrast" | "rejected_invalid_hex";

function applyBranding(brand: BrandBlock, startedAtMs: number): ApplyOutcome {
  let contrast;
  try {
    contrast = validateContrast(brand.primaryColor, brand.textOnPrimaryColor);
  } catch (e) {
    console.warn(
      `BrandingProvider: dropping brand for slug "${brand.slug}" — invalid hex (${
        e instanceof Error ? e.message : String(e)
      })`,
    );
    return "rejected_invalid_hex";
  }
  if (!contrast.pass) {
    console.warn(
      `BrandingProvider: dropping brand for slug "${brand.slug}" — contrast ${contrast.roundedRatio}:1 < 4.5:1`,
    );
    return "rejected_contrast";
  }
  const root = document.documentElement.style;
  root.setProperty("--brand-primary", brand.primaryColor);
  root.setProperty("--brand-secondary", brand.secondaryColor);
  root.setProperty("--brand-text-on-primary", brand.textOnPrimaryColor);
  const primaryHsl = hexToHslComponents(brand.primaryColor);
  const secondaryHsl = hexToHslComponents(brand.secondaryColor);
  root.setProperty("--primary", primaryHsl);
  root.setProperty("--secondary", secondaryHsl);
  root.setProperty("--ring", primaryHsl);
  document.title = brand.displayName;
  let icon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    icon.type = "image/png";
    document.head.appendChild(icon);
  }
  icon.href = brand.logoUrl;
  performance.mark("brand-applied");
  // WHY: the brand-hydration-post-mount measure is the post-mount engineering-lens
  // substrate; it needs branding-mount-started to exist. Try/catch so a missing start
  // mark (defensive — the effect emits it first thing) cannot throw inside the
  // brand-application path itself; the measure is non-critical, brand application is.
  try {
    performance.measure("brand-hydration-post-mount", "branding-mount-started", "brand-applied");
  } catch {
    /* swallow — measure is non-critical */
  }
  Sentry.setMeasurement("brand_hydration_ms", performance.now() - startedAtMs, "millisecond");
  return "applied";
}

export function BrandingProvider({ children }: { children: ReactNode; language?: string }) {
  const [branding, setBranding] = useState<BrandBlock | null>(null);
  // WHY initial = (slug !== null): on the canonical domain we never fetch,
  // so children render at first paint. On a branded subdomain we start in
  // loading state until the fetch settles OR the 3 s ceiling fires.
  const [isLoading, setIsLoading] = useState(() => extractBrandSlug(window.location.hostname) !== null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // WHY: React 18 StrictMode invokes effects twice in dev. Guard against double-apply
  // so document.title/favicon/marks aren't emitted twice.
  const appliedRef = useRef(false);
  // WHY: outcome is tagged at multiple sites (apply, malformed, fetch_error, ceiling).
  // The 3 s ceiling can fire in the same microtask as the fetch's terminal outcome;
  // without this guard the ceiling tag would overwrite the real failure signal.
  const outcomeTaggedRef = useRef(false);

  function tagOutcomeOnce(outcome: string) {
    if (outcomeTaggedRef.current) return;
    outcomeTaggedRef.current = true;
    Sentry.setTag("brand_outcome", outcome);
  }

  useEffect(() => {
    if (appliedRef.current) return;
    const slug = extractBrandSlug(window.location.hostname);
    if (!slug) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const startedAtMs = performance.now();
    // WHY: emit the start-mark BEFORE the await so the post-mount-engineering-lens
    // measure() inside applyBranding has a paired start to subtract from.
    performance.mark("branding-mount-started");
    (async () => {
      try {
        // apiClient.get<T> returns T directly (it unwraps response.data internally),
        // and its request interceptor prepends `/${language}` to every URL — so this
        // path is ONLY the post-`/v1/{language}/` portion. Anything fuller would
        // double-prefix.
        const payload = await apiClient.get<unknown>(
          `/public/branding/by-slug/${encodeURIComponent(slug)}`,
        );
        if (cancelled || appliedRef.current) return;
        if (hasRedirectFields(payload)) {
          appliedRef.current = true;
          setIsRedirecting(true);
          const target = payload.redirectTo
            ? `https://${payload.redirectTo}.gcheckin.com${window.location.pathname}${window.location.search}`
            : `https://gcheckin.com${window.location.pathname}${window.location.search}`;
          window.location.replace(target);
          return;
        }
        if (hasActiveBrandFields(payload)) {
          appliedRef.current = true;
          const outcome = applyBranding(payload, startedAtMs);
          tagOutcomeOnce(outcome);
          if (outcome === "applied") setBranding(payload);
        } else {
          tagOutcomeOnce("malformed_response");
        }
      } catch (err) {
        // WHY: apiClient's response interceptor rewrites axios errors through
        // formatApiError to ErrorApiDto { name, code, message, errorId } — there is
        // NO err.response on what callers see in the form-app. Read err.code (0 for
        // network error, HTTP status for response errors).
        Sentry.addBreadcrumb({
          category: "branding",
          level: "warning",
          message: `BrandingProvider fetch failed for slug "${slug}"`,
          data: { code: (err as { code?: number }).code },
        });
        tagOutcomeOnce("fetch_error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // WHY effect deps = []: brand resolution is locale-independent. apiClient's
    // own language sync (via i18n.on('languageChanged')) handles the URL prefix;
    // we don't need to re-fetch on locale change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WHY 3 s ceiling: cap worst-case visible-loading window so a hung backend converts
  // to "default GCI render" rather than "permanently-blank page". Same fail-graceful
  // target as the fetch's catch branch, just time-bounded.
  useEffect(() => {
    if (!isLoading) return;
    const slug = extractBrandSlug(window.location.hostname);
    if (slug === null) return;
    const ceilingMs = 3000;
    const id = window.setTimeout(() => {
      if (appliedRef.current) return;
      tagOutcomeOnce("fallback_ceiling_hit");
      setIsLoading(false);
    }, ceilingMs);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // WHY: render null while redirecting so children don't briefly paint with default
  // branding between fetch resolution and window.location.replace unload.
  if (isRedirecting) return null;

  const slug = extractBrandSlug(window.location.hostname);
  if (slug !== null && isLoading && !appliedRef.current) {
    return (
      <BrandingContext.Provider
        value={{ branding: null, isLoading: true, isBranded: false, isRedirecting: false }}
      >
        <BrandHydrationFallback />
      </BrandingContext.Provider>
    );
  }

  return (
    <BrandingContext.Provider
      value={{ branding, isLoading, isBranded: branding !== null, isRedirecting }}
    >
      {children}
    </BrandingContext.Provider>
  );
}
